import {
  ReactNode, useContext, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import RebalanceReportTable from './shared/rebalanceReportTable/rebalanceReportTable';
import {
  ModelPortfolioTypes,
  RebalanceReportColumn,
  PortfolioTypes,
  PortfolioReports,
  TriggerTypes,
  PortfolioStatus,
  PendingSubTradeRequests,
  SubTradeRequestValue,
  RebalanceBulkTradeRunReasons,
  TradeVerification,
} from './shared/sharedTypes';
import { PortfolioReport } from '../../../../interfaces';
import {
  Card, CardContent, Button, MenuItem, TextField,
} from '../../../2-component';
import { UserContext } from '../../../../providers/userContextProvider';
import { FETCH_PORTFOLIO_REPORTS, RUN_AUTO_REBALANCE } from '../queries/queriesMutations';
import {
  AvailableForCashColumn,
  ClientColumn,
  ExpectedCashColumn, FundTransferColumn,
  GoalColumn, LastUpdatedColumn,
  ModelColumn, OrganizationColumn,
  PortfolioTypeColumn, SMAIncludedColumn, TotalMarketValueColumn, TriggersColumn,
  isGoal,
} from './shared/rebalanceColumns';
import { Grid, Typography } from '../../../1-primative';
import { ModelPortfoliosSelect } from '../../../3-pattern';
import PortfolioAdjustmentsModal, { PortfolioAdjustmentActiveItem } from '../../rebalanceReport/components/modal';
import { PageObjectType } from '../../pageConfiguration/pageConfiguration';

// const TESTID_REBALANCE_REPORT_TABLE = 'rebalance-report-v2-table';
// const TESTID_REBALANCE_REPORT_TABLE_HEAD_CELL = (cell: string) => `${TESTID_REBALANCE_REPORT_TABLE}-head-cell-${cell}`;
// const TESTID_REBALANCE_REPORT_TABLE_ROW = (index: number) => `${TESTID_REBALANCE_REPORT_TABLE}-row-${index}`;
// const TESTID_REBALANCE_REPORT_TABLE_ROW_CELL = (index: number, cell: string) => `${TESTID_REBALANCE_REPORT_TABLE}-row-${index}-cell-${cell}`;
// const TESTID_REBALANCE_REPORT_TABLE_FILTER = (filter: string) => `${TESTID_REBALANCE_REPORT_TABLE}-filter-${filter}`;

const RebalancePage = ({ useInlinePortfolioTable = true }: { useInlinePortfolioTable?: boolean }) => {
  const [data, setData] = useState<PortfolioReport[]>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15);
  const [sortColumn, setSortColumn] = useState<keyof PortfolioReport | null>('createdAt');
  const [sorDirection, setSorDirection] = useState<'asc' | 'desc' | undefined>('desc');
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selected, setSelected] = useState<PortfolioReport[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeItem, setActiveItem] = useState<PortfolioAdjustmentActiveItem | undefined>();

  const [portfolioStatus, setPortfolioStatus] = useState<keyof typeof PortfolioStatus>(PortfolioStatus.ACTIVE);
  const [triggerType, setTriggerType] = useState<TriggerTypes>(TriggerTypes.ANY);
  const [portifolioType, setPortifolioType] = useState<keyof typeof PortfolioTypes>(PortfolioTypes.ANY);
  const [model, setModel] = useState<keyof typeof ModelPortfolioTypes>(ModelPortfolioTypes.ANY);
  const [pendingRequests, setPendingRequests] = useState<keyof typeof PendingSubTradeRequests>(PendingSubTradeRequests.ANY);
  const [subTradeRequestValue, setSubTradeRequestValue] = useState<keyof typeof SubTradeRequestValue>(SubTradeRequestValue.GREATER_THAN_ZERO);
  const [bulkTradeReason, setBulkTradeReason] = useState<keyof typeof RebalanceBulkTradeRunReasons>(RebalanceBulkTradeRunReasons.ANY);
  const [tradeVerification, setTradeVerification] = useState<keyof typeof TradeVerification>(TradeVerification.ANY);

  const { activeOrganization } = useContext(UserContext);

  const { t } = useTranslation(['rebalanceReportV2']);

  const [runAutoRebalance] = useMutation(RUN_AUTO_REBALANCE);
  const [autoRebalanceLoading, setAutoRebalanceLoading] = useState(false);

  const navigate = useNavigate();

  const columns: RebalanceReportColumn<PortfolioReport>[] = [
    {
      header: t('columns.client'), accessor: 'client', sort: false, cell: (row): ReactNode => (<ClientColumn data={row} />),
    }, {
      header: t('columns.portfolioType'), accessor: 'type', sort: false, cell: (row): ReactNode => (<PortfolioTypeColumn data={row} />),
    }, {
      header: t('columns.goal'), accessor: 'goal', sort: false, cell: (row): ReactNode => (<GoalColumn data={row} />),
    }, {
      header: t('columns.model'), accessor: 'portfolio', sort: false, cell: (row): ReactNode => (<ModelColumn data={row} />),
    }, {
      header: t('columns.availableCashForTrade'), accessor: 'cashAvailableForTradeCents', sort: true, cell: (row): ReactNode => (<AvailableForCashColumn data={row} />),
    }, {
      header: t('columns.expectedCash'), accessor: 'expectedCashCents', sort: true, cell: (row): ReactNode => (<ExpectedCashColumn data={row} />),
    }, {
      header: t('columns.totalMarketValue'), accessor: 'totalMarketValue', sort: true, cell: (row): ReactNode => (<TotalMarketValueColumn data={row} />),
    }, {
      header: t('columns.triggers'), accessor: 'triggers', sort: false, cell: (row): ReactNode => (<TriggersColumn data={row} />),
    }, {
      header: t('columns.smaIncluded'), accessor: 'smaIncluded', sort: false, visible: false, cell: (row): ReactNode => (<SMAIncludedColumn data={row} />),
    }, {
      header: t('columns.fundTransfer'), accessor: 'fundTransfer', sort: false, visible: false, cell: (row): ReactNode => (<FundTransferColumn data={row} />),
    }, {
      header: t('columns.organization'), accessor: 'organization', sort: false, cell: (row): ReactNode => (<OrganizationColumn data={row} />),
    }, {
      header: t('columns.lastUpdated'), accessor: 'updatedAt', sort: false, cell: (row): ReactNode => (<LastUpdatedColumn data={row} />),
    }, {
      header: t('columns.createdAt'), accessor: 'createdAt', visible: false, sorted: 'asc', sort: true, cell: (row): ReactNode => (<LastUpdatedColumn data={row} />),
    },
  ];

  const { loading, refetch } = useQuery(FETCH_PORTFOLIO_REPORTS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
          state: portfolioStatus !== PortfolioStatus.ANY ? portfolioStatus : undefined,
          anyRebalanceRequired: triggerType === TriggerTypes.BOTH ? true : undefined,
          cashTrigger: triggerType === TriggerTypes.CASH ? true : undefined,
          driftRebalanceRequired: triggerType === TriggerTypes.DRIFT ? true : undefined,
          modelPortfolioType: portifolioType !== PortfolioTypes.ANY ? portifolioType : undefined,
          modelPortfolioId: model !== ModelPortfolioTypes.ANY ? model : undefined,
          value: subTradeRequestValue !== 'ANY' ? subTradeRequestValue : undefined,
          pendingSubTradeRequests: pendingRequests !== 'ANY' ? pendingRequests === 'YES' : undefined,
          bulkTradeRequiredReason: bulkTradeReason !== 'ANY' ? bulkTradeReason : undefined,
          tradeVerificationStatus: tradeVerification !== 'ANY' ? tradeVerification : undefined,
        },
        pagination: {
          sortField: sortColumn,
          sortDesc: sorDirection === 'desc',
          perPage: itemsPerPage,
          offSet: (currentPage - 1) * itemsPerPage,
        },
      },
    },
    onCompleted: (response: PortfolioReports<PortfolioReport[]>) => {
      if (response?.fetchPortfolioReports?.portfolioReports) {
        setData(response.fetchPortfolioReports.portfolioReports);
        setTotalItems(response.fetchPortfolioReports.totalCount);
      }
    },
  });

  const clearFilters = (): void => {
    setPortfolioStatus(PortfolioStatus.ACTIVE);
    setTriggerType(TriggerTypes.ANY);
    setPortifolioType(PortfolioTypes.ANY);
    setModel(ModelPortfolioTypes.ANY);
    setPendingRequests(PendingSubTradeRequests.YES);
    setSubTradeRequestValue(SubTradeRequestValue.GREATER_THAN_ZERO);
    setBulkTradeReason(RebalanceBulkTradeRunReasons.ANY);
    setTradeVerification(TradeVerification.ANY);
  };

  const listenPage = (page: number, rowsPerPage: number): void => {
    setItemsPerPage(rowsPerPage);
    setCurrentPage(page);
    setSelected([]);
  };

  const listenSort = (column: keyof PortfolioReport | null, direction: 'asc' | 'desc' | undefined): void => {
    setSortColumn(column);
    setSorDirection(direction);
    setSelected([]);
  };

  const listenSelected = (tableData: PortfolioReport[]): void => {
    setSelected(tableData);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setActiveItem(undefined);
  };

  // Helper function to merge nested objects safely
  const mergeNestedObject = (original: any, updated: any) => {
    if (!original) return updated;
    if (!updated) return original;
    return { ...original, ...updated };
  };

  // Helper function to update portfolio data in the table
  const updatePortfolioData = (portfolioReport: PortfolioReport) => {
    const index = data?.findIndex((item) => item.id === portfolioReport.id);
    if (index === -1) return;

    const newData = [...data];
    const originalRow = data[index];

    const updatedRow: PortfolioReport = {
      ...originalRow,
      ...portfolioReport,
      object: mergeNestedObject(originalRow.object, portfolioReport.object),
      modelPortfolio: mergeNestedObject(originalRow.modelPortfolio, portfolioReport.modelPortfolio),
      subAccounts: portfolioReport.subAccounts || originalRow.subAccounts,
      holdings: portfolioReport.holdings || originalRow.holdings,
      bulkTradeRunReasons: portfolioReport.bulkTradeRunReasons || originalRow.bulkTradeRunReasons,
    };

    newData[index] = updatedRow;
    setData(newData);
  };

  useEffect(() => { setIsAllSelected(false); }, [selected]);

  const onAutoRebalance = async () => {
    if (isEmpty(selected) && !isAllSelected) { return; }

    setAutoRebalanceLoading(true);

    try {
      if (isAllSelected) {
        await runAutoRebalance({
          variables: {
            input: {
              organizationId: activeOrganization.id,
            },
          },
        });
      } else {
        await runAutoRebalance({
          variables: {
            input: {
              organizationId: activeOrganization.id,
              portfolioReportIds: selected.map((portifolio) => portifolio.id),
            },
          },
        });
      }

      setAutoRebalanceLoading(false);
      setSelected([]);
      refetch();
    } catch (error) {
      setSelected([]);
      setAutoRebalanceLoading(false);
    }
  };

  // Row click handler
  const handleRowClick = (row: PortfolioReport) => {
    setActiveItem({
      type: isGoal(row) ? PageObjectType.GOAL : PageObjectType.SUB_ACCOUNT,
      id: isGoal(row)
        ? (!isEmpty(row.object?.goal) ? row.object.goal.id : row.object.id)
        : row.object.id,
      portfolioReportId: row.id,
    });
    if (!useInlinePortfolioTable) {
      setModalOpen(true);
    } else if (isGoal(row)) {
      navigate(`/rebalanceReportV2/goal/${!isEmpty(row.object.goal) ? row.object.goal.id : row.object.id}`);
    } else {
      navigate(`/rebalanceReportV2/subAccount/${row.object.id}`);
    }
  };

  useEffect(() => {
    refetch();
  }, [currentPage, itemsPerPage, sortColumn, sorDirection, triggerType, portifolioType, model, refetch]);

  return (
    <Grid container gap={0}>
      <Grid item xs={12} mb={2}>
        <Card loading={loading || autoRebalanceLoading}>
          <CardContent>
            <Grid container gap={1} flexWrap='nowrap' mb={2}>
              <Grid item xs={3}>
                <TextField
                  select
                  fullWidth
                  label={t('filters.status')}
                  value={portfolioStatus}
                  onChange={(e: any) => setPortfolioStatus(e.target.value)}>
                  <MenuItem value={PortfolioStatus.ANY}>{t('common.any')}</MenuItem>
                  <MenuItem value={PortfolioStatus.ACTIVE}>{t('filters.active')}</MenuItem>
                  <MenuItem value={PortfolioStatus.INACTIVE}>{t('filters.inactive')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  select
                  fullWidth
                  label={t('filters.triggerType')}
                  value={triggerType}
                  onChange={(e: any) => setTriggerType(e.target.value)}>
                  <MenuItem value={TriggerTypes.BOTH}>{t('filters.anyRebalanceRequired')}</MenuItem>
                  <MenuItem value={TriggerTypes.DRIFT}>{t('filters.driftRebalance')}</MenuItem>
                  <MenuItem value={TriggerTypes.CASH}>{t('filters.cash')}</MenuItem>
                  <MenuItem value={TriggerTypes.ANY}>{t('common.any')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  select
                  fullWidth
                  label='Model'
                  value={portifolioType}
                  onChange={(e: any) => setPortifolioType(e.target.value)}>
                  <MenuItem value={PortfolioTypes.ANY}>{t('common.any')}</MenuItem>
                  <MenuItem value={PortfolioTypes.GOAL}>{t('common.goal')}</MenuItem>
                  <MenuItem value={PortfolioTypes.SUB_ACCOUNT}>{t('common.subAccount')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={3}>
                <ModelPortfoliosSelect
                  label={t('filters.selectPortfolio')}
                  value={model}
                  onChange={(e: any) => setModel(e.target.value)}
                  includeAnyOption />
              </Grid>
            </Grid>
            <Grid container gap={1} flexWrap='nowrap' mb={2}>
              <Grid item xs={activeOrganization.requireSubTradeRequestApproval ? 3 : 4}>
                <TextField
                  select
                  fullWidth
                  label={t('filters.value')}
                  value={subTradeRequestValue}
                  onChange={(e: any) => setSubTradeRequestValue(e.target.value)}>
                  <MenuItem value={SubTradeRequestValue.ANY}>{t('common.any')}</MenuItem>
                  <MenuItem value={SubTradeRequestValue.ZERO}>{t('filters.zero')}</MenuItem>
                  <MenuItem value={SubTradeRequestValue.GREATER_THAN_ZERO}>{t('filters.greaterThanZero')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={activeOrganization.requireSubTradeRequestApproval ? 3 : 4}>
                <TextField
                  select
                  fullWidth
                  label={t('filters.tradeRequests')}
                  value={pendingRequests}
                  onChange={(e: any) => setPendingRequests(e.target.value)}>
                  <MenuItem value={PendingSubTradeRequests.YES}>{t('filters.yes')}</MenuItem>
                  <MenuItem value={PendingSubTradeRequests.NO}>{t('filters.no')}</MenuItem>
                  <MenuItem value={PendingSubTradeRequests.ANY}>{t('common.any')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={activeOrganization.requireSubTradeRequestApproval ? 3 : 4}>
                <TextField
                  select
                  fullWidth
                  label={t('filters.bulkTradeRequiredReason')}
                  value={bulkTradeReason}
                  onChange={(e: any) => setBulkTradeReason(e.target.value)}>
                  <MenuItem value={RebalanceBulkTradeRunReasons.ANY}>{t('common.any')}</MenuItem>
                  <MenuItem value={RebalanceBulkTradeRunReasons.UNPAIRED}>{t('tooltips.UNPAIRED')}</MenuItem>
                  <MenuItem value={RebalanceBulkTradeRunReasons.PAIRED}>{t('tooltips.PAIRED')}</MenuItem>
                  <MenuItem value={RebalanceBulkTradeRunReasons.ILLIQUID}>{t('tooltips.ILLIQUID')}</MenuItem>
                </TextField>
              </Grid>
              {activeOrganization.requireSubTradeRequestApproval && (
                <Grid item xs={3}>
                  <TextField
                    select
                    fullWidth
                    label={t('filters.tradeVerificationStatus')}
                    value={tradeVerification}
                    onChange={(e: any) => setTradeVerification(e.target.value)}>
                    <MenuItem value={TradeVerification.ANY}>{t('common.any')}</MenuItem>
                    <MenuItem value={TradeVerification.VERIFIED}>{t('filters.verified')}</MenuItem>
                    <MenuItem value={TradeVerification.UNVERIFIED}>{t('filters.unverified')}</MenuItem>
                  </TextField>
                </Grid>
              )}
            </Grid>
            <Grid container gap={0} justifyContent='flex-end'>
              <Grid item xs='auto'>
                <Button
                  onClick={() => clearFilters()}
                  label={t('actions.clearFilters')}
                  variant='filled'
                  color='primary'
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card loading={loading}>
          <CardContent>
            <Grid container justifyContent='space-between' alignItems='center'>
              <Grid item xs='auto'>
                { (data.length && selected.length === data.length) ? (
                  <Grid container gap={1} alignItems='center'>
                    <Grid item xs='auto'>
                      <Typography>All <strong>{ data.length }</strong> rows on this page are selected.</Typography>
                    </Grid>
                    <Grid item xs='auto'>
                      <Button
                        size='xs'
                        onClick={() => { setIsAllSelected((value) => !value); }}
                        label={isAllSelected ? t('actions.unselectAllPages') : t('actions.selectedAllPages')}
                        variant='outlined'
                      />
                    </Grid>
                    <Grid item xs='auto'>
                      <Button
                        size='xs'
                        onClick={() => { setIsAllSelected((value) => !value); }}
                        label={isAllSelected ? 'Unselect all pages' : 'Selected all pages'}
                        variant='outlined'
                      />
                    </Grid>
                  </Grid>
                ) : null}
              </Grid>
              <Grid item xs='auto'>
                <Button
                  onClick={() => { onAutoRebalance(); }}
                  label={t('actions.autoRebalance')}
                  variant='filled'
                  disabled={!selected.length}/>
              </Grid>
            </Grid>
          </CardContent>
          <RebalanceReportTable<PortfolioReport>
            data={data}
            columns={columns}
            loading={loading}
            checkbox={true}
            pagination={true}
            linesPerPage={itemsPerPage}
            initialPage={currentPage}
            totalLines={totalItems}
            onPage={listenPage}
            onSort={listenSort}
            onSelect={listenSelected}
            onTableRowClick={handleRowClick}
          />
        </Card>
      </Grid>
      {activeItem && modalOpen && (
        <PortfolioAdjustmentsModal
          item={activeItem}
          open={modalOpen}
          handleClose={handleModalClose}
          onPortfolioRefresh={(portfolioReport: PortfolioReport) => {
            updatePortfolioData(portfolioReport);
          }}
        />
      )}
    </Grid>
  );
};

export default RebalancePage;
